import React, { useState, useEffect, useCallback, useRef } from "react";
import UserLogsStyle from "./css/UserLogsStyle";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import axiosInstance from "../../variables/api/axiosInstance";

const ACTION_CONFIG = {
    CONNEXION: { color: "#22d3ee", icon: "→", glow: "0 0 8px rgba(34,211,238,0.4)" },
    CRÉATION: { color: "#34d399", icon: "+", glow: "0 0 8px rgba(52,211,153,0.4)" },
    MODIFICATION: { color: "#fbbf24", icon: "~", glow: "0 0 8px rgba(251,191,36,0.4)" },
    SUPPRESSION: { color: "#f87171", icon: "×", glow: "0 0 8px rgba(248,113,113,0.4)" },
};

const UserLogging = () => {
    const [logs, setLogs] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(3);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ username: "", action: "", startDate: "", endDate: "" });
    const [isLive, setIsLive] = useState(true);
    const [selectedLog, setSelectedLog] = useState(null);
    const [terminalLines, setTerminalLines] = useState([]);
    const terminalRef = useRef(null);
    const [clock, setClock] = useState(new Date());
    const [animatedRows, setAnimatedRows] = useState(new Set());

    // Chargement initial des logs
    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page,
                size: 20,
                ...filters,
            };
            const response = await axiosInstance.get("/api/logging/utilisateurs", { params });
            setLogs(response.data.content);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            console.error("Erreur lors du chargement des logs:", error);
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    // Live clock
    useEffect(() => {
        const t = setInterval(() => setClock(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    // Boot sequence
    useEffect(() => {
        const bootMessages = [
            { text: "PoolIns Logging System v4.2.1", delay: 100 },
            { text: "Initialisation du module de surveillance...", delay: 400 },
            { text: "Connexion au serveur SSE: ws://poolins-api/stream", delay: 700 },
            { text: "Authentification réussie. Canal ouvert.", delay: 1100 },
            { text: `${logs.length} entrées chargées depuis le cache.`, delay: 1400 },
            { text: "▓ Surveillance en temps réel ACTIVE", delay: 1700 },
        ];
        bootMessages.forEach(({ text, delay }) => {
            setTimeout(() => {
                setTerminalLines((prev) => [...prev, { text, time: new Date() }]);
            }, delay);
        });
    }, [logs]);

    // Auto-scroll terminal
    useEffect(() => {
        if (terminalRef.current) {
            terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
        }
    }, [terminalLines]);

    // Animate rows on mount
    useEffect(() => {
        logs.forEach((_, i) => {
            setTimeout(() => setAnimatedRows((prev) => new Set([...prev, i])), i * 60);
        });
    }, [logs]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleSearch = () => {
        setPage(0);
        setTerminalLines((prev) => [
            ...prev,
            { text: `QUERY> filtre appliqué: ${JSON.stringify(filters)}`, time: new Date() },
        ]);
    };

    const handleReset = () => {
        setFilters({ username: "", action: "", startDate: "", endDate: "" });
        setPage(0);
        setTerminalLines((prev) => [
            ...prev,
            { text: "RESET> filtres réinitialisés", time: new Date() },
        ]);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        const d = new Date(dateString);
        return d.toLocaleString("fr-FR", {
            year: "numeric", month: "2-digit", day: "2-digit",
            hour: "2-digit", minute: "2-digit", second: "2-digit",
        });
    };

    const formatTime = (date) => {
        return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    };

    const getPerformer = (log) => {
        if (log.action === "CRÉATION") return log.createdBy;
        if (log.action === "SUPPRESSION") return log.deletedBy;
        return log.updatedBy || "—";
    };

    const stats = {
        CONNEXION: logs.filter((l) => l.action === "CONNEXION").length,
        CRÉATION: logs.filter((l) => l.action === "CRÉATION").length,
        MODIFICATION: logs.filter((l) => l.action === "MODIFICATION").length,
        SUPPRESSION: logs.filter((l) => l.action === "SUPPRESSION").length,
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <>
                            <UserLogsStyle />

                            <div className="log-root">
                                <div className="log-container">

                                    {/* ── Header ── */}
                                    <div className="log-header">
                                        <div className="log-header-left">
                                            <div className="log-header-dots">
                                                <span className="dot-red" />
                                                <span className="dot-yellow" />
                                                <span className="dot-green" />
                                            </div>
                                            <div className="log-title-block">
                                                <h1>poolins://admin/logs/utilisateurs</h1>
                                                <span>Session active — PID 4821 — {logs.length} entrées chargées</span>
                                            </div>
                                        </div>
                                        <div className="log-header-right">
                                            <span className="log-clock">{formatTime(clock)}</span>
                                            <button
                                                className={`live-badge ${isLive ? "active" : "inactive"}`}
                                                onClick={() => setIsLive(!isLive)}
                                            >
                                                <span className="live-dot" />
                                                {isLive ? "LIVE" : "PAUSED"}
                                            </button>
                                        </div>
                                    </div>

                                    {/* ── Mini Terminal ── */}
                                    <div className="mini-terminal" ref={terminalRef}>
                                        {terminalLines.map((line, i) => (
                                            <div className="term-line" key={i}>
                                                <span className="term-time">[{formatTime(line.time)}]</span>
                                                <span className="term-prompt">$</span>
                                                <span className="term-text">{line.text}</span>
                                            </div>
                                        ))}
                                        <div className="term-line" style={{ color: "var(--term-green)" }}>
                                            <span className="term-prompt">$</span>
                                            <span style={{ animation: "livePulse 1s ease infinite" }}>▌</span>
                                        </div>
                                    </div>

                                    {/* ── Stats ── */}
                                    <div className="stats-bar">
                                        {Object.entries(ACTION_CONFIG).map(([action, config]) => (
                                            <div className="stat-item" key={action}>
                                                <div
                                                    className="stat-icon"
                                                    style={{
                                                        background: `${config.color}15`,
                                                        color: config.color,
                                                        border: `1px solid ${config.color}30`,
                                                    }}
                                                >
                                                    {config.icon}
                                                </div>
                                                <div className="stat-info">
                                                    <div className="stat-count" style={{ color: config.color }}>
                                                        {stats[action] || 0}
                                                    </div>
                                                    <div className="stat-label">{action}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* ── Filters ── */}
                                    <div className="filter-bar">
                                        <span className="filter-prefix">FILTER &gt;</span>
                                        <input
                                            className="filter-input"
                                            type="text"
                                            name="username"
                                            placeholder="utilisateur..."
                                            value={filters.username}
                                            onChange={handleFilterChange}
                                            style={{ width: 140 }}
                                        />
                                        <select
                                            className="filter-select"
                                            name="action"
                                            value={filters.action}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">toutes actions</option>
                                            <option value="CRÉATION">création</option>
                                            <option value="MODIFICATION">modification</option>
                                            <option value="CONNEXION">connexion</option>
                                            <option value="SUPPRESSION">suppression</option>
                                        </select>
                                        <input
                                            className="filter-input"
                                            type="datetime-local"
                                            name="startDate"
                                            value={filters.startDate}
                                            onChange={handleFilterChange}
                                        />
                                        <span style={{ color: "var(--term-muted)", fontSize: 11 }}>→</span>
                                        <input
                                            className="filter-input"
                                            type="datetime-local"
                                            name="endDate"
                                            value={filters.endDate}
                                            onChange={handleFilterChange}
                                        />
                                        <div className="filter-sep" />
                                        <button className="filter-btn primary" onClick={handleSearch}>
                                            ⏎ exec
                                        </button>
                                        <button className="filter-btn" onClick={handleReset}>
                                            ↺ reset
                                        </button>
                                    </div>

                                    {/* ── Table ── */}
                                    <div className="log-table-wrap">
                                        <table className="log-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 40 }}>#</th>
                                                    <th>Horodatage</th>
                                                    <th>Utilisateur</th>
                                                    <th>Email</th>
                                                    <th>Action</th>
                                                    <th>Effectué par</th>
                                                    <th>Profil</th>
                                                    <th>Agence</th>
                                                    <th>État</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr className="visible">
                                                        <td colSpan={9}>
                                                            <div className="log-loading">
                                                                <span className="loading-spinner">⟳</span>
                                                                Chargement des entrées...
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : logs.length === 0 ? (
                                                    <tr className="visible">
                                                        <td colSpan={9}>
                                                            <div className="log-empty">
                                                                $ query returned 0 results — aucune entrée correspondante
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    logs.map((log, index) => {
                                                        const cfg = ACTION_CONFIG[log.action] || { color: "#6b7280", icon: "?", glow: "none" };
                                                        return (
                                                            <tr
                                                                key={`${log.codeId}-${index}`}
                                                                className={`${animatedRows.has(index) ? "visible" : ""} ${selectedLog === index ? "selected" : ""}`}
                                                                onClick={() => setSelectedLog(selectedLog === index ? null : index)}
                                                            >
                                                                <td className="log-line-num">{page * 20 + index + 1}</td>
                                                                <td className="log-time">{formatDate(log.updatedAt || log.createdAt)}</td>
                                                                <td className="log-user">{log.username}</td>
                                                                <td className="log-email">{log.email}</td>
                                                                <td>
                                                                    <span
                                                                        className="action-tag"
                                                                        style={{
                                                                            background: `${cfg.color}12`,
                                                                            color: cfg.color,
                                                                            border: `1px solid ${cfg.color}25`,
                                                                        }}
                                                                    >
                                                                        <span className="action-icon">{cfg.icon}</span>
                                                                        {log.action}
                                                                    </span>
                                                                </td>
                                                                <td className="log-by">{getPerformer(log)}</td>
                                                                <td className="log-profil">{log.profil || "—"}</td>
                                                                <td className="log-agence">{log.agence || "—"}</td>
                                                                <td>
                                                                    {log.active ? (
                                                                        <span><span className="status-dot status-active" />Actif</span>
                                                                    ) : (
                                                                        <span><span className="status-dot status-inactive" />Inactif</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* ── Detail Panel ── */}
                                    {selectedLog !== null && logs[selectedLog] && (
                                        <div className="detail-panel">
                                            <div className="detail-title">
                                                <span>▸ Détails de l'entrée #{page * 20 + selectedLog + 1}</span>
                                                <button className="detail-close" onClick={() => setSelectedLog(null)}>
                                                    [×] fermer
                                                </button>
                                            </div>
                                            <div className="detail-grid">
                                                <div className="detail-field">
                                                    <label>Code ID</label>
                                                    <span>{logs[selectedLog].codeId}</span>
                                                </div>
                                                <div className="detail-field">
                                                    <label>Utilisateur</label>
                                                    <span style={{ color: "var(--term-cyan)" }}>{logs[selectedLog].username}</span>
                                                </div>
                                                <div className="detail-field">
                                                    <label>Email</label>
                                                    <span>{logs[selectedLog].email}</span>
                                                </div>
                                                <div className="detail-field">
                                                    <label>Action</label>
                                                    <span style={{ color: ACTION_CONFIG[logs[selectedLog].action]?.color }}>
                                                        {logs[selectedLog].action}
                                                    </span>
                                                </div>
                                                <div className="detail-field">
                                                    <label>Créé par</label>
                                                    <span>{logs[selectedLog].createdBy || "—"}</span>
                                                </div>
                                                <div className="detail-field">
                                                    <label>Modifié par</label>
                                                    <span>{logs[selectedLog].updatedBy || "—"}</span>
                                                </div>
                                                <div className="detail-field">
                                                    <label>Supprimé par</label>
                                                    <span style={{ color: logs[selectedLog].deletedBy ? "var(--term-red)" : "inherit" }}>
                                                        {logs[selectedLog].deletedBy || "—"}
                                                    </span>
                                                </div>
                                                <div className="detail-field">
                                                    <label>Profil</label>
                                                    <span>{logs[selectedLog].profil || "—"}</span>
                                                </div>
                                                <div className="detail-field">
                                                    <label>Agence</label>
                                                    <span>{logs[selectedLog].agence || "—"}</span>
                                                </div>
                                                <div className="detail-field">
                                                    <label>Créé le</label>
                                                    <span>{formatDate(logs[selectedLog].createdAt)}</span>
                                                </div>
                                                <div className="detail-field">
                                                    <label>Mis à jour le</label>
                                                    <span>{formatDate(logs[selectedLog].updatedAt)}</span>
                                                </div>
                                                <div className="detail-field">
                                                    <label>Statut</label>
                                                    <span style={{ color: logs[selectedLog].active ? "var(--term-green)" : "var(--term-red)" }}>
                                                        {logs[selectedLog].active ? "● Actif" : "● Inactif"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* ── Footer / Pagination ── */}
                                    <div className="log-footer">
                                        <span className="page-info">
                                            Page {page + 1}/{totalPages} — {logs.length} entrées affichées
                                        </span>
                                        <div className="page-controls">
                                            <button
                                                className="page-btn"
                                                disabled={page === 0}
                                                onClick={() => setPage(0)}
                                            >
                                                ⟨⟨
                                            </button>
                                            <button
                                                className="page-btn"
                                                disabled={page === 0}
                                                onClick={() => setPage(page - 1)}
                                            >
                                                ⟨ Préc
                                            </button>
                                            {[...Array(totalPages)].map((_, i) => (
                                                <button
                                                    key={i}
                                                    className={`page-btn ${page === i ? "active" : ""}`}
                                                    onClick={() => setPage(i)}
                                                >
                                                    {i + 1}
                                                </button>
                                            ))}
                                            <button
                                                className="page-btn"
                                                disabled={page === totalPages - 1}
                                                onClick={() => setPage(page + 1)}
                                            >
                                                Suiv ⟩
                                            </button>
                                            <button
                                                className="page-btn"
                                                disabled={page === totalPages - 1}
                                                onClick={() => setPage(totalPages - 1)}
                                            >
                                                ⟩⟩
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    </div>
                    <Footer />
                    {/* <!-- / Footer --> */}

                    <div className="content-backdrop fade"></div>
                </div>
            </div>

        </div>
    );
};

export default UserLogging;